import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo } from "@/mocks/content";

const FORM_URL = "https://readdy.ai/api/form/db9j8rs6oi3g3tv8uvvg";

const services = [
  {
    icon: "ri-file-text-line",
    title: "剧情介绍整理",
    text: "按集数整理剧情摘要、关键看点与人物关系，帮助你在追剧前后快速回顾。",
  },
  {
    icon: "ri-user-star-line",
    title: "演员资料档案",
    text: "收录主要与配角演员资料、饰演角色与代表作品，了解角色背后的表演。",
  },
  {
    icon: "ri-newspaper-line",
    title: "影视资讯追踪",
    text: "收视表现、主创专访、幕后花絮与取景地盘点，持续更新剧集动态。",
  },
];

const promises = [
  "不提供视频播放、下载与任何付费服务",
  "不收集与内容浏览无关的个人信息",
  "所有剧情与演员资料均为整理与介绍性质",
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("website_alt") || "").trim();
    if (guard) {
      setStatus("success");
      setFeedback("留言已收到，我们会尽快回复你。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value);
      if (!text.trim()) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        setFeedback("留言已提交，感谢你的反馈，我们会尽快回复。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          isSpam
            ? "提交未通过校验，请检查内容后重新提交。"
            : `提交失败：${serverMessage || "请稍后重试"}`,
        );
      }
    } catch (error) {
      setStatus("error");
      setFeedback(
        `提交失败：${error instanceof Error ? error.message : "网络异常，请稍后重试"}`,
      );
    }
  };

  return (
    <PageShell>
      <PageMeta
        title="关于我们 - 嫂子的诱惑影视内容站"
        description="了解嫂子的诱惑内容站的定位、服务内容与联系方式。本站专注电视剧剧情介绍、演员资料与影视资讯整理，不提供视频播放与下载服务。"
        keywords="嫂子的诱惑,关于我们,影视内容站,剧情介绍,演员资料"
      />

      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10 lg:px-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-6 grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-end">
            <div>
              <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.24em] text-accent-700">
                <span className="h-px w-6 bg-accent-400" />
                About us
              </p>
              <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-[40px]">
                关于「嫂子的诱惑」
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {siteInfo.intro}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-foreground-400">
                内容定位
              </p>
              <p className="mt-2 font-heading text-lg font-semibold text-foreground-950">
                专业影视内容站 · 剧集资料整理
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                面向《嫂子的诱惑》观众与影视爱好者，提供剧情、演员与资讯三类内容，支持站内搜索与分类浏览。
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-14 lg:px-10">
        <section className="grid gap-4 md:grid-cols-3">
          {services.map((service) => (
            <div
              key={service.title}
              className="rounded-lg border border-background-200/80 bg-background-50 p-5"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className={`${service.icon} text-lg`} />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {service.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{service.text}</p>
            </div>
          ))}
        </section>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_1fr]">
          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              我们做什么，不做什么
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站以文字资料为核心，希望让观众在追剧时少一点混乱、多一点理解。我们不转载完整视频内容，也不提供任何绕过官方渠道的观看方式。
            </p>
            <ul className="mt-5 space-y-3">
              {promises.map((promise) => (
                <li key={promise} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line mt-0.5 text-base text-primary-500" />
                  {promise}
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-lg border border-background-200/80 bg-background-100/70 p-5">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">联系方式</h3>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-base text-accent-700" />
                  {siteInfo.contact.email}
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-base text-accent-700" />
                  {siteInfo.contact.workTime}
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-base text-accent-700" />
                  {siteInfo.contact.address}
                </li>
              </ul>
            </div>

            <div className="mt-6 rounded-lg bg-primary-900 p-5 text-background-100">
              <p className="text-[10px] uppercase tracking-[0.24em] text-accent-300">内容说明</p>
              <p className="mt-2 text-xs leading-relaxed text-background-100/70">
                站内剧情摘要、演员资料与资讯均为演示数据，用于展示页面结构与内容形态。剧集相关版权归原制作方与播出平台所有，如有侵权请联系我们删除。
              </p>
            </div>
          </section>

          <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              给我们留言
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              内容纠错、合作咨询或想看的内容建议，都可以通过下面的表单告诉我们。
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="mt-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-xs font-medium text-foreground-700">
                    称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-xs font-medium text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-type" className="text-xs font-medium text-foreground-700">
                  留言类型
                </label>
                <div className="relative mt-1.5">
                  <select
                    id="contact-type"
                    name="topic"
                    defaultValue="内容纠错"
                    className="h-10 w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="演员资料补充">演员资料补充</option>
                    <option value="影视资讯投稿">影视资讯投稿</option>
                    <option value="合作咨询">合作咨询</option>
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="text-xs font-medium text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的问题或建议（最多 500 字）"
                  className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                />
                <p className="mt-1 text-[11px] text-foreground-400">最多 500 字，请勿填写敏感信息。</p>
              </div>

              <div className="field-office-note" aria-hidden="true">
                <label htmlFor="contact-office-note">办公区备注</label>
                <input
                  id="contact-office-note"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-6"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin text-base" : "ri-send-plane-line text-base"} />
                {status === "submitting" ? "提交中..." : "提交留言"}
              </button>

              {feedback && (
                <p
                  className={`mt-3 rounded-md px-3 py-2 text-xs leading-relaxed ${
                    status === "success"
                      ? "bg-accent-100 text-accent-900"
                      : "bg-primary-50 text-primary-800"
                  }`}
                >
                  {feedback}
                </p>
              )}
              <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
                提交即表示你同意我们通过邮箱回复你的留言。我们不会将信息用于其他用途。
              </p>
            </form>
          </section>
        </div>

        <section className="mt-12 rounded-lg border border-background-200/80 bg-background-100/70 p-5 md:p-7">
          <h2 className="font-heading text-lg font-bold text-foreground-950">版权说明与免责声明</h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">
            <p>
              本站为非商业性质的影视内容介绍站点，站内文字内容由编辑整理撰写，剧照与封面图片仅用于内容展示与说明。电视剧《嫂子的诱惑》及相关素材的版权归原制作方、出品方及播出平台所有。
            </p>
            <p>
              本站不提供视频播放、下载、录播或任何形式的在线观看服务，站内标注的“在线观看”相关表述仅用于描述内容主题，不构成观看入口。如需观看正片，请前往官方授权播出平台。
            </p>
            <p>
              站内剧情摘要、演员资料与资讯均为演示数据，可能与实际剧集内容存在差异，仅供浏览参考。如权利方认为内容存在侵权，请通过上方联系方式与我们联系，我们将在核实后及时处理。
            </p>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line text-base" />
              返回首页
            </Link>
            <Link
              to={`/category/${encodeURIComponent("影视资讯")}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览影视资讯
              <i className="ri-arrow-right-line text-base" />
            </Link>
          </div>
        </section>
      </div>
    </PageShell>
  );
}